import { useTranslation } from 'react-i18next';

import { renderNamespace } from 'new-components/forms/LockdownUserConfigForm/helpers';
import { useIsRestrictedBySiteSpecificRole } from 'utils/customHooks/useIsRestrictedBySiteSpecificRole';
import { useShowSitePartitioningItems } from 'utils/customHooks/useShowSitePartitioningItems';

import { OpDataFetchSelect } from '../OpDataFetchSelect/OpDataFetchSelect';
import { OpTooltip } from '../OpTooltip/OpTooltip';

import { OpSiteSpecificUserDataFetchSelectProps } from './types';

/**
 * Component that wraps `OpDataFetchSelect` to fetch and display users that would
 * normally not have data due to site-specific access limitations.
 *
 * @param {OpSiteSpecificUserDataFetchSelectProps} props - The properties for the component.
 * @param {string} props.orgId - The organization ID used to fetch site data.
 * @param {object} props.opDataFetchSelectProps - Additional properties passed to `OpDataFetchSelect`.
 *
 * @returns {JSX.Element} The rendered `OpSiteSpecificUserDataFetchSelect` component.
 *
 * @remarks
 * It uses the `useIsRestrictedBySiteSpecificRole` hook to determine if the user has
 * site-specific restrictions and modifies the options and labels accordingly.
 * The `createDisabledOption` function disables options for users restricted by site-specific roles
 * the `createOptionLabel` function displays a tooltip with the user's email and namespace or a
 * restriction message if the user is restricted.
 */
export const OpSiteSpecificUserDataFetchSelect = ({
  orgId,
  ...opDataFetchSelectProps
}: OpSiteSpecificUserDataFetchSelectProps) => {
  const { t } = useTranslation();
  const checkSiteSpecificRoleRestriction =
    useIsRestrictedBySiteSpecificRole(orgId);
  const { isShowingSitePartitioningItems } =
    useShowSitePartitioningItems(orgId);

  return (
    <OpDataFetchSelect
      queryOptions={{
        apiEndpointName: 'listUsers',
        parameters: isShowingSitePartitioningItems
          ? [orgId, { filter: 'status:(!=I)', options: 'withUserSites' }]
          : [orgId, { filter: 'status:(!=I)' }],
      }}
      fetchAllInitialValues={{
        /**
         * Pass ignoreSiteSpecificAccess to fetch all users so that we can create options for
         * any users that may have been restricted by site-specific access. Creating the options
         * allows us to display the user name as well as disabling the option if the user has
         * site-specific restrictions.
         */
        ...(isShowingSitePartitioningItems && {
          queryParamOverrides: {
            options: 'withUserSites,ignoreSiteSpecificAccess',
          },
        }),
      }}
      createDisabledOption={({ sites }) =>
        checkSiteSpecificRoleRestriction(sites?.map(({ id }) => id))
      }
      // eslint-disable-next-line react/no-unstable-nested-components
      createOptionLabel={({ id: userId, identity, sites: userSites }) => (
        <OpTooltip
          title={
            checkSiteSpecificRoleRestriction(userSites?.map(({ id }) => id))
              ? t('Your site-specific role prevents management of this user')
              : `${identity?.email} (${renderNamespace(identity)})`
          }
        >
          {identity?.fullName || identity?.email || userId}
        </OpTooltip>
      )}
      {...opDataFetchSelectProps}
    />
  );
};
